import { YStack, Text, Select, Spinner, CounterInput, Button } from '@holo/ui';
import { formatPrice } from '@holo/format';
import { useEffect, useRef, useState } from 'react';
import { useCart } from '@/hooks';
import type { ProductVariantType } from '@holo/gql';

type ProductVariant = {
  id: string;
  price: number;
  unit: {
    name: string;
    quantity: number;
  };
  name?: string;
  type: ProductVariantType;
};

type ProductControlProps = {
  variants: ProductVariant[];
  name: string;
  category: string;
  subCategory: string;
  image?: string;
};

const ProductControl = ({ variants, name, image, category, subCategory }: ProductControlProps) => {
  const autoFocus = useRef(false);
  const { addItem, removeItem, updateItem, cart, loading: cartLoading } = useCart();
  const [loading, setLoading] = useState(true);
  const [variantId, setVariantId] = useState(variants[0]?.id);
  const selectedVariant = variants.find((variant) => variant.id === variantId);
  const quantity = cart?.items?.find((item) => item.id === selectedVariant?.id)?.quantity || 0;

  // NOTE: For some reason we get miss match because of tamagui component and a combination
  // Of a fast response (?), changing CounterInput to a plain div everything works
  // And also with a local state here it works perfectly, need more investigation
  useEffect(() => {
    setLoading(false);
  }, [cartLoading]);

  if (!selectedVariant) {
    return null;
  }

  const handleAddItem = () => {
    autoFocus.current = true;

    addItem({
      id: selectedVariant.id,
      image,
      name,
      variantName: selectedVariant?.name,
      quantity: 1,
      unitQuantity: selectedVariant.unit.quantity,
      unit: selectedVariant.unit.name,
      price: selectedVariant.price,
      category,
      subCategory,
      type: selectedVariant.type,
    });
  };

  const handleUpdateQuantity = (value: number) => {
    if (value) {
      updateItem({
        quantity: value,
        id: selectedVariant.id,
      });
    } else {
      removeItem(selectedVariant.id);
    }
  };

  return (
    <YStack space="$4" alignItems="center" $sm={{ flexDirection: 'row' }} mt="$10" width="100%">
      {variants.length > 1 ? (
        <YStack width="100%" $sm={{ maxWidth: 250, flex: 1 }}>
          <Select
            variant="rounded"
            centered
            placeholder="Select a variant"
            items={variants.map((v) => ({
              id: v.id,
              label: `${formatPrice({ amount: v.price || 0 })} / ${v?.unit.quantity > 1 ? v?.unit.quantity : ''} ${
                v?.name || v?.unit?.name
              }`,
            }))}
            selectedItemId={selectedVariant.id}
            onSelect={(item) => {
              if (item?.id && item.id !== selectedVariant.id) {
                setVariantId(item.id);
              }
            }}
          />
        </YStack>
      ) : (
        <Text
          width="100%"
          $sm={{ maxWidth: 250, flex: 1 }}
          borderWidth={1}
          borderColor="$border"
          borderRadius={10000}
          height="$10"
          display="flex"
          jc="center"
          ai="center"
          px="$4"
          fontWeight="$medium"
        >{`${formatPrice({
          amount: selectedVariant.price || 0,
        })} / ${selectedVariant.unit.quantity > 1 ? selectedVariant.unit.quantity : ''} ${
          selectedVariant.name || selectedVariant.unit.name
        }`}</Text>
      )}

      {!loading && quantity > 0 ? (
        <CounterInput
          autoFocus={autoFocus.current}
          size="lg"
          value={quantity}
          onChange={handleUpdateQuantity}
          wrapperProps={{ width: '100%', flex: 1, $sm: { maxWidth: 250, flex: 1 } }}
        />
      ) : (
        <Button
          width="100%"
          $sm={{ maxWidth: 250, flex: 1 }}
          icon={loading ? undefined : 'shoppingCart'}
          onPress={handleAddItem}
        >
          {loading ? <Spinner color="$background" /> : 'Add to cart'}
        </Button>
      )}
    </YStack>
  );
};

export default ProductControl;
